import { faDownToLine as DownloadIcon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Stack, Typography } from '@mui/material';
import type { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { filesize } from 'filesize';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';

import ContentNotification from '@/components/ContentNotfication';
import LargeCard from '@/components/LargeCard';
import LoadingIndicator from '@/components/LoadingIndicator';
import { useGridTheme } from '@/hooks/useGridTheme';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';
import { userIsInternalAtom } from '@/store/user.store';

import { useFetchDownloads } from './AdministrationDownloads.store';
import type {
  DownloadButtonsProps,
  DownloadLinks,
  DownloadSectionProps,
  LogAgentRowData,
  LogAgentTableProps
} from './AdministrationDownloads.types';

const handleDownload = async (orgLabel: string, path: string) => {
  const response = await axiosClient.get(
    `organizations/${orgLabel}/link?path=${path}`
  );
  window.open(response.data.link, '_blank');
};

const LogAgentTable = ({ links, type, orgLabel }: LogAgentTableProps) => {
  const gridTheme = useGridTheme().withParams({
    autoHeightMinBodyHeight: 0
  });
  const sortedFileNames = Object.keys(links).sort();

  const rowData: LogAgentRowData[] = useMemo(
    () =>
      sortedFileNames.map((key) => {
        const [lastUpdated, os, filename, size] = key.split('|');
        return {
          type: os,
          filename,
          size,
          lastUpdated,
          downloadPath: `${type}%2F${key}%2F${links[key]}`
        };
      }),
    [sortedFileNames, type, links]
  );

  const columnDefs: ColDef<LogAgentRowData>[] = useMemo(
    () => [
      {
        field: 'type',
        headerName: 'Type',
        width: 150,
        cellStyle: { textTransform: 'capitalize' }
      },
      {
        field: 'filename',
        headerName: 'Filename',
        flex: 1,
        cellRenderer: (params: { data: LogAgentRowData }) => (
          <Button
            size="small"
            startIcon={<FontAwesomeIcon icon={DownloadIcon} />}
            onClick={() => handleDownload(orgLabel, params.data.downloadPath)}
          >
            {params.data.filename}
          </Button>
        )
      },
      {
        field: 'size',
        headerName: 'Size',
        width: 150,
        valueFormatter: (params) =>
          filesize(params.value, { standard: 'jedec', pad: true })
      },
      {
        field: 'lastUpdated',
        headerName: 'Last Updated',
        width: 180
      }
    ],
    [orgLabel]
  );

  return (
    <Box sx={{ width: '50%', minWidth: '50rem' }}>
      <AgGridReact
        columnDefs={columnDefs}
        domLayout="autoHeight"
        rowData={rowData}
        rowHeight={48}
        suppressCellFocus
        theme={gridTheme}
      />
    </Box>
  );
};

const DownloadButtons = ({ links, type, orgLabel }: DownloadButtonsProps) => {
  const userIsInternal = useAtomValue(userIsInternalAtom);
  const sortedFileNames = useMemo(() => {
    let names = Object.keys(links).sort();

    // For ENG-6947 it was decided to display the reports file download only to internal users.
    if (!userIsInternal) {
      names = names.filter((name) => !name.includes('reports'));
    }

    return names;
  }, [links, userIsInternal]);

  if (sortedFileNames.length === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'flex-start', flexWrap: 'wrap', pt: 1 }}
      spacing={2}
    >
      {sortedFileNames.map((fileName) => {
        const downloadPath = `${type}%2F${fileName}%2F${links[fileName]}`;

        return (
          <Button
            key={fileName}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={DownloadIcon} />}
            onClick={() => handleDownload(orgLabel, downloadPath)}
          >
            {fileName.toUpperCase()}
          </Button>
        );
      })}
    </Stack>
  );
};

const DownloadSection = ({
  title,
  links,
  type,
  orgLabel,
  subtitle,
  isLogAgent = false
}: DownloadSectionProps) => (
  <Box>
    <Typography variant="h6">{title.toUpperCase()}</Typography>

    {subtitle && (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 1, maxWidth: '120ch' }}
      >
        {subtitle}
      </Typography>
    )}

    <Box sx={{ mb: 4, overflowX: 'auto' }}>
      {isLogAgent ? (
        <LogAgentTable links={links} type={type} orgLabel={orgLabel} />
      ) : (
        <DownloadButtons links={links} type={type} orgLabel={orgLabel} />
      )}
    </Box>
  </Box>
);

const AdministrationDownloads = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);

  const { data: images, isFetching: isLoadingImages } =
    useFetchDownloads('images');
  const { data: logAgents, isFetching: isLoadingLogAgent } =
    useFetchDownloads('log_agents');

  const isLoading = isLoadingImages || isLoadingLogAgent;
  const hasNoOrganization = selectedOrganization.label === 'all';

  if (hasNoOrganization) {
    return (
      <ContentNotification
        title="Select an organization"
        text="Please select an organization to view and edit its downloads."
      />
    );
  }

  const baseImagesSubtitle = `For Pondurance deployments, delete the base image once your server is up. The image is
                              meant only for initial deployment; storing or reusing it later can result in outdated systems.
                              Your running device gets updates from Pondurance. Always download a current image for any
                              future installations.`;

  const hasLogAgents = Object.keys(logAgents?.log_agents || {}).length > 0;
  const hasHostAgents = Object.keys(logAgents?.host_agents || {}).length > 0;
  const hasRansomSnaresAgents =
    Object.keys(logAgents?.ransom_snares || {}).length > 0;

  return (
    <LargeCard title="Downloads">
      <Stack spacing={2.5}>
        {!isLoading && (
          <>
            {images?.base_images && (
              <DownloadSection
                title="Base Images"
                links={images.base_images as DownloadLinks}
                type="images/base_images"
                orgLabel={selectedOrganization.label}
                subtitle={baseImagesSubtitle}
              />
            )}

            {images?.vmpe_images && (
              <DownloadSection
                title="EVMP Images"
                links={images.vmpe_images as DownloadLinks}
                type="images/vmpe_images"
                orgLabel={selectedOrganization.label}
                subtitle="Images for Enhanced VMP Service"
              />
            )}

            {hasLogAgents && (
              <DownloadSection
                title="Log Agents"
                links={logAgents?.log_agents as DownloadLinks}
                type="log_agents"
                orgLabel={selectedOrganization.label}
                isLogAgent
              />
            )}

            {hasHostAgents && (
              <DownloadSection
                title="Host Agents"
                links={logAgents?.host_agents as DownloadLinks}
                type="log_agents"
                orgLabel={selectedOrganization.label}
                isLogAgent
              />
            )}

            {hasRansomSnaresAgents && (
              <DownloadSection
                title="RansomSnares Agent"
                links={logAgents?.ransom_snares as DownloadLinks}
                type="ransom_snares"
                orgLabel={selectedOrganization.label}
                isLogAgent
              />
            )}
          </>
        )}
        <LoadingIndicator isLoading={isLoading} />
      </Stack>
    </LargeCard>
  );
};

export default AdministrationDownloads;
